"use client";

import { useState } from "react";

interface Feature {
    id: string;
    label: string;
}

interface Props {
    features: Feature[];
}

type Timeline = "ready_now" | "within_1_month" | "1_3_months" | "3_plus_months";

const TIMELINE_OPTIONS: { value: Timeline; label: string; sub: string }[] = [
    { value: "ready_now",       label: "Már kész vagyok",        sub: "Átköltöznék most" },
    { value: "within_1_month",  label: "1 hónapon belül",        sub: "Hamarosan indulok" },
    { value: "1_3_months",      label: "1–3 hónap múlva",        sub: "Még tervezem" },
    { value: "3_plus_months",   label: "3 hónapnál később",      sub: "Egyelőre csak figyelem" },
];

type Step = "timeline" | "features" | "email";
type Status = "idle" | "loading" | "success" | "conflict" | "error";

export function SignupForm({ features }: Props) {
    const [step, setStep] = useState<Step>("timeline");
    const [timeline, setTimeline] = useState<Timeline | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [email, setEmail] = useState("");
    const [ownIdea, setOwnIdea] = useState("");
    const [status, setStatus] = useState<Status>("idle");

    const toggleFeature = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const submit = async () => {
        if (!email || !timeline) return;
        setStatus("loading");

        try {
            const res = await fetch("/api/pilot-signup", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email,
                    start_timeline: timeline,
                    important_features: Array.from(selected),
                    own_idea: ownIdea.trim() || null,
                }),
            });

            if (res.status === 409) { setStatus("conflict"); return; }
            if (!res.ok) { setStatus("error"); return; }
            setStatus("success");
        } catch {
            setStatus("error");
        }
    };

    // Sikeres beküldés
    if (status === "success") {
        return (
            <div className="text-center py-8 flex flex-col items-center gap-4">
                <div className="text-5xl">🚀</div>
                <h3 className="text-2xl font-black" style={{ letterSpacing: "-0.03em" }}>
                    Feliratkoztál.
                </h3>
                <p style={{ color: "#94a3b8" }}>
                    Értesítünk, amint a korai hozzáférés elérhető.
                    <br />Május 1-jén találkozunk.
                </p>
            </div>
        );
    }

    // Duplikált email
    if (status === "conflict") {
        return (
            <div className="text-center py-8 flex flex-col items-center gap-4">
                <div className="text-4xl">✉️</div>
                <h3 className="text-xl font-bold">Ez az email már regisztrált.</h3>
                <p style={{ color: "#94a3b8" }}>Hamarosan találkozunk.</p>
                <button onClick={() => setStatus("idle")} className="text-sm underline" style={{ color: "#7c3aed" }}>
                    Más emailcímmel próbálom
                </button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-8">
            {/* Step indicator */}
            <div className="flex items-center gap-2">
                {(["timeline", "features", "email"] as Step[]).map((s, i) => (
                    <div key={s} className="flex items-center gap-2">
                        <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                            style={{
                                background: step === s ? "#7c3aed" : (["timeline","features","email"].indexOf(step) > i ? "rgba(124,58,237,0.3)" : "#1e1e3f"),
                                color: step === s ? "#fff" : "#475569",
                            }}
                        >
                            {i + 1}
                        </div>
                        {i < 2 && <div className="flex-1 h-px w-8" style={{ background: ["timeline","features","email"].indexOf(step) > i ? "rgba(124,58,237,0.5)" : "#1e1e3f" }} />}
                    </div>
                ))}
            </div>

            {/* STEP 1: Timeline */}
            {step === "timeline" && (
                <div className="flex flex-col gap-4">
                    <h3 className="text-lg font-bold" style={{ letterSpacing: "-0.02em" }}>
                        Mikor szeretnél elindulni?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TIMELINE_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => setTimeline(opt.value)}
                                className="text-left rounded-xl p-4 transition-all"
                                style={{
                                    background: timeline === opt.value ? "rgba(124,58,237,0.2)" : "#0d0d1f",
                                    border: `1px solid ${timeline === opt.value ? "rgba(124,58,237,0.6)" : "#1e1e3f"}`,
                                    color: timeline === opt.value ? "#c4b5fd" : "#94a3b8",
                                }}
                            >
                                <div className="font-semibold text-sm">{opt.label}</div>
                                <div className="text-xs mt-0.5" style={{ color: timeline === opt.value ? "#a78bfa" : "#475569" }}>{opt.sub}</div>
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setStep("features")}
                        disabled={!timeline}
                        className="w-full py-3.5 rounded-xl font-bold text-sm transition-all mt-2"
                        style={{
                            background: timeline ? "#7c3aed" : "#1e1e3f",
                            color: timeline ? "#fff" : "#475569",
                            cursor: timeline ? "pointer" : "not-allowed",
                        }}
                    >
                        Következő →
                    </button>
                </div>
            )}

            {/* STEP 2: Features */}
            {step === "features" && (
                <div className="flex flex-col gap-4">
                    <div>
                        <h3 className="text-lg font-bold" style={{ letterSpacing: "-0.02em" }}>
                            Mi a legfontosabb számodra?
                        </h3>
                        <p className="text-xs mt-1" style={{ color: "#475569" }}>
                            Válassz ki akár több funkciót is – ez befolyásolja a fejlesztési prioritásokat.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin", scrollbarColor: "#1e1e3f transparent" }}>
                        {features.map((f) => (
                            <button
                                key={f.id}
                                onClick={() => toggleFeature(f.id)}
                                className="text-left rounded-lg px-4 py-3 text-sm transition-all flex items-center gap-3"
                                style={{
                                    background: selected.has(f.id) ? "rgba(124,58,237,0.15)" : "#0d0d1f",
                                    border: `1px solid ${selected.has(f.id) ? "rgba(124,58,237,0.5)" : "#1e1e3f"}`,
                                    color: selected.has(f.id) ? "#c4b5fd" : "#94a3b8",
                                }}
                            >
                                <span className="flex-shrink-0 w-4 h-4 rounded flex items-center justify-center text-xs"
                                    style={{ background: selected.has(f.id) ? "#7c3aed" : "#1e1e3f" }}>
                                    {selected.has(f.id) ? "✓" : ""}
                                </span>
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-3 mt-2">
                        <button onClick={() => setStep("timeline")} className="px-5 py-3 rounded-xl text-sm font-semibold"
                            style={{ background: "#0d0d1f", border: "1px solid #1e1e3f", color: "#64748b" }}>
                            ← Vissza
                        </button>
                        <button onClick={() => setStep("email")} className="flex-1 py-3 rounded-xl font-bold text-sm"
                            style={{ background: "#7c3aed", color: "#fff" }}>
                            Következő →
                        </button>
                    </div>
                </div>
            )}

            {/* STEP 3: Email + own idea */}
            {step === "email" && (
                <div className="flex flex-col gap-4">
                    <h3 className="text-lg font-bold" style={{ letterSpacing: "-0.02em" }}>
                        Hova küldjük a meghívót?
                    </h3>

                    <input
                        type="email"
                        placeholder="email@cimed.hu"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all"
                        style={{
                            background: "#0d0d1f",
                            border: "1px solid #2a2a4a",
                            color: "#f8fafc",
                            caretColor: "#7c3aed",
                        }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(124,58,237,0.6)")}
                        onBlur={(e) => (e.target.style.borderColor = "#2a2a4a")}
                    />

                    <textarea
                        placeholder="Van saját ötleted? (nem kötelező)"
                        value={ownIdea}
                        onChange={(e) => setOwnIdea(e.target.value)}
                        rows={3}
                        className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all resize-none"
                        style={{
                            background: "#0d0d1f",
                            border: "1px solid #2a2a4a",
                            color: "#f8fafc",
                            caretColor: "#7c3aed",
                        }}
                        onFocus={(e) => (e.target.style.borderColor = "rgba(124,58,237,0.6)")}
                        onBlur={(e) => (e.target.style.borderColor = "#2a2a4a")}
                    />

                    {status === "error" && (
                        <p className="text-sm" style={{ color: "#f87171" }}>
                            Hiba történt. Próbáld újra.
                        </p>
                    )}

                    <div className="flex gap-3">
                        <button onClick={() => setStep("features")} className="px-5 py-3 rounded-xl text-sm font-semibold"
                            style={{ background: "#0d0d1f", border: "1px solid #1e1e3f", color: "#64748b" }}>
                            ← Vissza
                        </button>
                        <button
                            onClick={submit}
                            disabled={!email || status === "loading"}
                            className="flex-1 py-4 rounded-xl font-bold text-sm transition-all"
                            style={{
                                background: email && status !== "loading" ? "linear-gradient(135deg, #7c3aed, #6d28d9)" : "#1e1e3f",
                                color: email && status !== "loading" ? "#fff" : "#475569",
                                cursor: email && status !== "loading" ? "pointer" : "not-allowed",
                                boxShadow: email && status !== "loading" ? "0 0 24px rgba(124,58,237,0.35)" : "none",
                            }}
                        >
                            {status === "loading" ? "Küldés..." : "Korai hozzáférés igénylése →"}
                        </button>
                    </div>

                    <p className="text-xs text-center" style={{ color: "#334155" }}>
                        Nem küldünk spamot. Az adataidat csak a pilot programhoz használjuk.
                    </p>
                </div>
            )}
        </div>
    );
}
